import { z } from "zod";
import { isoDateSchema, moneySchema, trimmed } from "./common";

const positiveMoney = moneySchema.refine((value) => value > 0, "Informe um valor maior que zero");

export const expenseRecurrences = ["once", "monthly"] as const;
export const expenseInputSchema = z.object({ category: trimmed(60), description: trimmed(100), amount: positiveMoney, date: isoDateSchema, recurrence: z.enum(expenseRecurrences) });
export type ExpenseInput = z.infer<typeof expenseInputSchema>;
export interface Expense extends ExpenseInput {
  id: string;
}

export const extraRevenueInputSchema = z.object({ source: trimmed(60), description: trimmed(100), amount: positiveMoney, date: isoDateSchema });
export type ExtraRevenueInput = z.infer<typeof extraRevenueInputSchema>;
export interface ExtraRevenue extends ExtraRevenueInput {
  id: string;
}

export interface FinanceMonth {
  month: string; // AAAA-MM
  invoiceRevenue: number;
  extraRevenue: number;
  expenses: number;
  result: number;
}

export interface FinanceSummary {
  from: string;
  to: string;
  invoiceRevenue: number;
  extraRevenue: number;
  revenue: number;
  expenses: number;
  result: number;
  pendingInvoices: { count: number; value: number };
  months: FinanceMonth[];
  expenseEntries: Expense[];
  extraRevenues: ExtraRevenue[];
}

/** Datas em que uma despesa ocorre dentro do período, respeitando a recorrência mensal. */
export function expenseOccurrences(expense: { date: string; recurrence: "once" | "monthly" }, from: string, to: string): string[] {
  if (from > to) return [];
  if (expense.recurrence === "once") return expense.date >= from && expense.date <= to ? [expense.date] : [];
  const [startYear, startMonth, startDay] = expense.date.split("-").map(Number) as [number, number, number];
  const [fromYear, fromMonth] = from.split("-").map(Number) as [number, number];
  const [toYear, toMonth] = to.split("-").map(Number) as [number, number];
  const occurrences: string[] = [];
  let year = Math.max(startYear, fromYear);
  let month = year === startYear && (startYear > fromYear || startMonth > fromMonth) ? startMonth : year === fromYear ? fromMonth : 1;
  if (year === startYear && month < startMonth) month = startMonth;
  while (year < toYear || (year === toYear && month <= toMonth)) {
    const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
    const occurrence = `${year}-${String(month).padStart(2, "0")}-${String(Math.min(startDay, lastDay)).padStart(2, "0")}`;
    if (occurrence >= expense.date && occurrence >= from && occurrence <= to) occurrences.push(occurrence);
    month += 1;
    if (month > 12) { month = 1; year += 1; }
  }
  return occurrences;
}

export function monthKeys(from: string, to: string): string[] {
  const keys: string[] = [];
  let [year, month] = from.split("-").map(Number) as [number, number];
  const [toYear, toMonth] = to.split("-").map(Number) as [number, number];
  while (year < toYear || (year === toYear && month <= toMonth)) {
    keys.push(`${year}-${String(month).padStart(2, "0")}`);
    month += 1;
    if (month > 12) { month = 1; year += 1; }
  }
  return keys;
}
